import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import type { Session } from "@supabase/supabase-js";

import { supabaseClient } from "../services/supabase";

import {
  getEmployeeProfile,
  signIn,
  signOut,
} from "./auth.service";

import type { EmployeeProfile } from "./types";

import {
  hasPermission,
  type Permission,
} from "./permissions";

import {
  logAuditEvent,
} from "../core/audit.service";

type AuthContextValue = {
  session: Session | null;

  profile: EmployeeProfile | null;

  loading: boolean;

  login: (
    identifier: string,
    password: string,
  ) => Promise<void>;

  logout: () => Promise<void>;

  can: (
    permission: Permission,
  ) => boolean;
};

const AuthContext =
  createContext<AuthContextValue | null>(
    null,
  );

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [session, setSession] =
    useState<Session | null>(null);

  const [profile, setProfile] =
    useState<EmployeeProfile | null>(
      null,
    );

  const [loading, setLoading] =
    useState(true);

  async function loadProfile(
    userId: string,
  ) {
    const employee =
      await getEmployeeProfile(userId);

    setProfile(employee);

    return employee;
  }

  useEffect(() => {
    let mounted = true;

    async function initialize() {
      try {
        const { data } =
          await supabaseClient.auth.getSession();

        if (!mounted) return;

        const currentSession =
          data.session ?? null;

        setSession(currentSession);

        if (currentSession?.user.id) {
          await loadProfile(
            currentSession.user.id,
          );
        }
      } catch (error) {
        console.error(
          "Falha ao carregar sessão:",
          error,
        );

        setProfile(null);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    initialize();

    const { data: listener } =
      supabaseClient.auth.onAuthStateChange(
        async (
          _event,
          nextSession,
        ) => {
          if (!mounted) return;

          setSession(nextSession);

          if (nextSession?.user.id) {
            try {
              await loadProfile(
                nextSession.user.id,
              );
            } catch (error) {
              console.error(error);

              setProfile(null);
            }
          } else {
            setProfile(null);
          }

          setLoading(false);
        },
      );

    return () => {
      mounted = false;

      listener.subscription.unsubscribe();
    };
  }, []);

  async function login(
    identifier: string,
    password: string,
  ) {
    const result =
      await signIn(
        identifier,
        password,
      );

    if (result.user) {
      const employee =
        await loadProfile(
          result.user.id,
        );

      await logAuditEvent({
        userId:
          result.user.id,

        employeeId:
          employee.id,

        employeeName:
          employee.nome,

        employeeLoginId:
          employee.login_id,

        eventType:
          "LOGIN",

        level:
          "success",

        module:
          "auth",

        entityType:
          "session",

        description:
          `${employee.nome} entrou no sistema.`,
      });
    }
  }

  async function logout() {
    if (
      session?.user &&
      profile
    ) {
      await logAuditEvent({
        userId:
          session.user.id,

        employeeId:
          profile.id,

        employeeName:
          profile.nome,

        employeeLoginId:
          profile.login_id,

        eventType:
          "LOGOUT",

        level:
          "info",

        module:
          "auth",

        entityType:
          "session",

        description:
          `${profile.nome} saiu do sistema.`,
      });
    }

    await signOut();

    setSession(null);
    setProfile(null);
  }

  function can(
    permission: Permission,
  ) {
    return hasPermission(
      profile?.cargo,
      permission,
    );
  }

  return (
    <AuthContext.Provider
      value={{
        session,
        profile,
        loading,
        login,
        logout,
        can,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context =
    useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth precisa estar dentro de AuthProvider.",
    );
  }

  return context;
}